/**
 * The GM windows of WINDOW_REGISTRY (registry.ts spreads them in, in this
 * order): the GM Event windows and the 🛡️ GM section. Split out of registry.ts
 * on 2026-10-08 (lane AK) so the registry stays under its file-size ceiling;
 * the entries moved verbatim. Pure data, ZERO React — same rules as registry.ts.
 */

import type { WindowDef } from './types';

export const GM_WINDOW_DEFS: readonly WindowDef[] = [
  // ── GM Event windows (category 'gm' since 2026-07-10 — DJ menu audit:
  //    they lived stranded under GAME WINDOWS while their control-surface
  //    siblings sat in the GM section) ──
  {
    kind: 'gameplay',
    id: 'gmEventAnnouncement',
    label: 'Event Announcement',
    icon: '📢',
    description: 'Active GM event announcement banner — title, phase, countdown, participants.',
    defaultSize: { width: 400, height: 200 },
    category: 'gm',
    persist: false,
  },
  {
    kind: 'gameplay',
    id: 'gmEventScoreboard',
    label: 'Event Scoreboard',
    icon: '🏆',
    description: 'Live scoreboard for the active GM event — player rankings by score.',
    defaultSize: { width: 450, height: 350 },
    category: 'gm',
    persist: false,
  },
  {
    kind: 'gameplay',
    id: 'raceLobby',
    label: 'Race Lobby',
    icon: '🏁',
    description: 'Live GM Race lobby — participant list, draggable framed rows, and Start Race (admin). Auto-opens on announced race events.',
    // M7: default below the top-center announcement banner (top:12, ~56px tall)
    // so it does not overlap; still draggable/positionable by the player.
    defaultPosition: { x: 100, y: 100 },
    defaultSize: { width: 360, height: 420 },
    minSize: { width: 280, height: 260 },
    category: 'gm',
    persist: false,
    // zIndex below the banner (9000) so announcements stay on top, but above
    // most HUD chrome (the ToolWindow default 200).
    zIndex: 8500,
  },
  // ── 🛡️ GM section (2026-07-06, DJ ask) ────────────────────────────────
  //
  // GM control surfaces — every window here is UI over the slash-command
  // registry (src/shared/constants/GMPermissions.ts COMMAND_PERMISSIONS), so
  // the server gates each action regardless of who opens the panel; buttons
  // grey out below the required tier as a courtesy.
  //
  // Section roadmap (add here, one window per concern):
  //   gmLobbyTools   ✅ roster + bot spawn/despawn/behavior (/bot — GM4)
  //   gmPlayerTools  ✅ self cheats: heal/mana/god/cd/level/xp/gold (GM3)
  //   gmEventManager ✅ Event Control — create + lifecycle in ONE state-aware
  //                     window (absorbed gmEventCreator, E.3 2026-07-10)
  //   gmWorldTools   ⬜ mob/NPC spawn + killnpc + loadzone (/spawn — GM3/4)
  //   gmModeration   ⬜ announce / setgm / pvp overrides (SUPPORT+/ADMIN)
  {
    kind: 'gameplay',
    id: 'gmEventManager',
    label: 'Event Control',
    icon: '🎪',
    description: 'State-aware GM event control — create from templates, run the lobby, drive phases, run it back.',
    defaultSize: { width: 400, height: 440 },
    category: 'gm',
    persist: false,
  },
  {
    kind: 'gameplay',
    id: 'gmLobbyTools',
    label: 'GM Lobby',
    icon: '🤖',
    description: 'Live room roster + bot spawn/despawn/behavior (/bot, GM4) — add players to the lobby for integration testing.',
    defaultSize: { width: 340, height: 460 },
    minSize: { width: 300, height: 320 },
    category: 'gm',
    persist: false,
  },
  {
    kind: 'gameplay',
    id: 'gmPlayerTools',
    label: 'GM Player',
    icon: '🧙',
    description: 'Self-targeting GM cheats as one-click buttons — heal, infinite mana, god, no-cooldowns, level/xp/gold (GM3).',
    defaultSize: { width: 320, height: 400 },
    minSize: { width: 280, height: 300 },
    category: 'gm',
    persist: false,
  },
  {
    kind: 'gameplay',
    id: 'gmHazardLab',
    label: 'Hazard Lab',
    icon: '🔨',
    description: 'Spawn race hazards at your position, outside any event (GM4) — clean isolated testing of moving hit shapes, impulses, and renderers. Pairs with the 🏁 Race Debug overlay.',
    defaultSize: { width: 320, height: 420 },
    minSize: { width: 280, height: 320 },
    category: 'gm',
    persist: false,
  },
  {
    kind: 'gameplay',
    id: 'gmSoundDebug',
    label: 'Sound Debug',
    icon: '🔊',
    description: 'THE sound card — every audio tool in one place: live fire-trace truth for ALL game sound (race + abilities + UI) with path/distance/pan/gain, the ⚙️ engine-health strip, solo test fires at known offsets (bright debug.sound_test chirp), the 🔉 emitter-rings overlay, and the folded 🎛️ Audio Lab bench (bus sliders + fire any synth/bank cue). "Why was that full volume / silent?" is one read.',
    defaultSize: { width: 380, height: 560 },
    minSize: { width: 320, height: 400 },
    // Dedicated 'sound' category (2026-07-11 DJ ask): one comprehensive
    // card in its own Shift+D section — audio truth is a dev-wide concern.
    // The standalone audioDebug card folded in here as the bench section.
    category: 'sound',
    urlSeed: ['soundTruth', 'soundDebug'],
    persist: false,
  },
];
